import { Utils } from "src/app/_helpers";
import { OrderNotificationParams } from "src/app/app.interfaces";
import { SmartExecutionTypeMap } from "src/app/app.structs";


export const OrderStatusGroups = {
    Confirmed: ['confirmed', 'replaced'],
    Scheduled: ['amo_pending', 'amo_replaced', 'schedule_new', 'schedule_pending',
        'schedule_replaced' ],
    Placed: ['pending_new', 'pending_replaced'],
    Partially_Filled: ['partially_filled'],
    Filled: ['filled'],
    Rejected: ['rejected', 'new_rejected', 'rms_reject', 'amo_rejected', 'replace_rejected', 
                'cancel_rejected', 'schedule_rejected', 'amo_replace_rejected'],
    Cancelled: ['cancelled', 'pending_cancel', 'schedule_cancelled','amo_cancelled'],
     // Add GTT status groups
     GTT_Confirmed: ['order_created'],
     GTT_Replaced: ['order_modified'],
     GTT_Cancelled: ['order_cancelled'],
     GTT_Triggered: ['order_triggered'],
     SUPER_CREATED: ['Created'],
     SUPER_ACTIVE: ['Active'],
     SUPER_CANCELLED: ['Cancelled'],
     SUPER_FAILED: ['Failed'],
     SUPER_COMPLETED: ['Completed'],
     SUPER_MODIFIED: ['Modified']
};

export const OrderStatusDisplayText = {
    Confirmed: 'Confirmed',
    Filled: 'Filled',
    Partially_Filled: 'Partially Filled',  // Space instead of underscore
    Rejected: 'Rejected',
    Cancelled: 'Cancelled',
    Scheduled: 'Scheduled',
    Placed: 'Placed',
    Replaced: 'Replaced',
    // GTT specific statuses
    GTT_Confirmed: 'Confirmed',
    GTT_Replaced: 'Replaced',
    GTT_Cancelled: 'Cancelled',
    GTT_Triggered: 'Triggered',

    SUPER_CREATED: 'Created',
    SUPER_MODIFIED: 'Modified',
    SUPER_ACTIVE: 'Active',
    SUPER_CANCELLED: 'Cancelled',
    SUPER_FAILED: 'Failed',
    SUPER_COMPLETED: 'Completed',
    // Smart order specific statuses
    Smart_Pending: 'Pending',
    Smart_Active: 'Active',
    Smart_Cancelled: 'Cancelled',
    Smart_Failed: 'Failed',
    Smart_Completed: 'Completed',
    Smart_Paused: 'Paused',
};

export const OrderStatusColors = {
    Confirmed: '#007bff',  // Blue
    Filled: '#4CAF50',    // Green
    Partially_Filled: '#4CAF50',    // Green
    Rejected: '#E31E24',  // Red
    Cancelled: '#FFA726',  // Orange
    Scheduled: '#007bff',  // Blue
    Placed: '#007bff',  // Blue,
    Replaced: '#007bff',  // Blue
    // GTT specific colors
    GTT_Confirmed: '#007bff', // Blue
    GTT_Replaced: '#007bff', // Blue
    GTT_Cancelled: '#FFA726', // Orange
    GTT_Triggered: '#4CAF50', // Green
    SUPER_CREATED: '#007bff', // Blue
    SUPER_ACTIVE: '#007bff', // Blue
    SUPER_CANCELLED: '#E31E24', // Orange
    SUPER_FAILED: '#E31E24', // Red
    SUPER_COMPLETED: '#4CAF50', // Green
    SUPER_MODIFIED: '#007bff', // Blue
    // Smart order specific colors
    Smart_Pending: '#007bff', // Blue
    Smart_Active: '#4CAF50', // Green
    Smart_Cancelled: '#FFA726', // Orange
    Smart_Failed: '#E31E24', // Red
    Smart_Completed: '#4CAF50', // Green
    Smart_Paused: '#FFA726', // Orange
};

export type OrderStatusGroupType = keyof typeof OrderStatusGroups;

export const orderMessageTemplates = {
    Confirmed: (order: OrderNotificationParams) => ({
        orderText: `${order.order_mode.toUpperCase()} ${order.formatted_symbol} is placed.`,
        statusText:'Please check order book for status.'
    }),
    Partially_Filled: (order: OrderNotificationParams) => ({
        orderText: `${order.order_mode.toUpperCase()} ${order.formatted_symbol} is partially filled.`,
        statusText: `${order.last_filled_qty} qty @ ${order.last_filled_price}`
    }),
    Filled: (order: OrderNotificationParams) => ({
        orderText: `${order.order_mode.toUpperCase()} ${order.formatted_symbol} is filled.`,
        statusText: `${order.quantity} qty @ ${order.last_filled_price}`
    }),
    Rejected: (order: OrderNotificationParams) => ({
        orderText: `${order.order_mode.toUpperCase()} ${order.quantity} qty ${order.formatted_symbol} has been rejected`,
        statusText: `Due to ${order.error_text}`
    }),
    Cancelled: (order: OrderNotificationParams) => ({
        orderText: `${order.order_mode.toUpperCase()} ${order.formatted_symbol} is cancelled.`,
        statusText: ''
    }),
    Scheduled: (order: OrderNotificationParams) => {
        // Different messages to be shown for AMO and scheduled orders.
        if (order.order_status.toLowerCase().includes('amo')) {
            return {
                orderText: `${order.order_mode.toUpperCase()} ${order.formatted_symbol} will be sent to the 
                    exchange at next exchange opening.`,
                statusText: ''
            };
        } else if (order.order_status.toLowerCase().includes('schedule')) {
            const scheduledTime = Utils.convertEpochToTimeString(order.scheduled_order_time);
            return {
                orderText: `${order.order_mode.toUpperCase()} ${order.formatted_symbol} will be sent to the 
                    exchange at ${scheduledTime}`,
                statusText: ''
            };
        }
    },
    GTT_Confirmed: (order: OrderNotificationParams) => ({
        orderText: `GTT ${order.order_mode} ${order.formatted_symbol} is created.`,
        statusText: `Your ${order.order_mode} GTT order for symbol ${order.formatted_symbol} has been created, please check GTT book for status.`
    }),
    GTT_Replaced: (order: OrderNotificationParams) => ({
        orderText: `GTT ${order.order_mode} ${order.formatted_symbol} is modified.`,
        statusText: `Your ${order.order_mode} GTT order for symbol ${order.formatted_symbol} has been modified, please check GTT book for status.`
    }),
    GTT_Cancelled: (order: OrderNotificationParams) => ({
        orderText: `GTT ${order.order_mode} ${order.formatted_symbol} is cancelled.`,
        statusText: `Your ${order.order_mode} GTT order for symbol ${order.formatted_symbol} has been cancelled, please check GTT book for status.`
    }),
    GTT_Triggered: (order: OrderNotificationParams) => {
        if (order.is_triggered) {
            return {
                orderText: `GTT order triggered.`,
                statusText: `Your ${order.order_mode} GTT order for symbol ${order.formatted_symbol} has been triggered of ${order.sl_quantity} qty @ ${order.sl_price}`
            }
        } else {
            return {
                orderText: `GTT order triggered.`,
                statusText: `Your ${order.order_mode} GTT order for symbol ${order.formatted_symbol} has been triggered of ${order.target_quantity} qty @ ${order.target_trigger_price}`
            }
        }
    },
    SUPER_CREATED: (order: OrderNotificationParams) => ({
        orderText: `Super order ${order.order_mode} ${order.formatted_symbol} is created.`,
        statusText: `Your ${order.order_mode} super order for symbol ${order.formatted_symbol} has been created, please check super book for status.`
    }),
    SUPER_MODIFIED: (order: OrderNotificationParams) => ({
        orderText: `Super order ${order.order_mode} ${order.formatted_symbol} is modified.`,
        statusText: `Your ${order.order_mode} super order for symbol ${order.formatted_symbol} is pending, please check super book for status.`
    }),
    SUPER_ACTIVE: (order: OrderNotificationParams) => ({
        orderText: `Super order is Active.`,
        statusText: `Your ${order.order_mode} super order for symbol ${order.formatted_symbol} has been marked as active, please check super book for status.`
    }),
    SUPER_CANCELLED: (order: OrderNotificationParams) => ({
        orderText: `Super order ${order.order_mode} ${order.formatted_symbol} is cancelled.`,
        statusText: `Your ${order.order_mode} super order for symbol ${order.formatted_symbol} has been cancelled, please check super book for status.`
    }),
    SUPER_FAILED: (order: OrderNotificationParams) => ({
        orderText: `Super order ${order.order_mode} ${order.formatted_symbol} is rejected.`,
        statusText: `Your ${order.order_mode} super order for symbol ${order.formatted_symbol} has been failed, please check super book for status.`
    }),
    SUPER_COMPLETED: (order: OrderNotificationParams) => ({
        orderText: `Super order is completed.`,
        statusText: `Your ${order.order_mode} super order for symbol ${order.formatted_symbol} has been filled, please check super book for status.`
    }),
    Smart_Pending: (order: OrderNotificationParams) => ({
        orderText: `Smart Order is scheduled.`,
        statusText: `Your ${SmartExecutionTypeMap[order.smart_order_type]} order for symbol ${order.formatted_symbol} is scheduled at ${order.smart_start_time}.`
    }),
    Smart_Active: (order: OrderNotificationParams) => ({
        orderText: `Smart order is active.`, // Your <execution type> for is acticated
        statusText: `Your ${SmartExecutionTypeMap[order.smart_order_type]} order for symbol ${order.formatted_symbol}, scheduled at ${order.smart_start_time} is active.`
    }),
    Smart_Cancelled: (order: OrderNotificationParams) => ({
        orderText: `Smart order is cancelled.`,
        statusText: `Your ${SmartExecutionTypeMap[order.smart_order_type]} order for symbol ${order.formatted_symbol} is cancelled.`
    }),
    Smart_Failed: (order: OrderNotificationParams) => ({
        orderText: `Smart order failed.`,
        statusText: `Your ${SmartExecutionTypeMap[order.smart_order_type]} order for symbol ${order.formatted_symbol} is failed.`
    }),
    Smart_Completed: (order: OrderNotificationParams) => ({
        orderText: `Smart order completed.`,
        statusText: `Your ${SmartExecutionTypeMap[order.smart_order_type]} order for symbol ${order.formatted_symbol} is completed.`
    }),
    Smart_Paused: (order: OrderNotificationParams) => ({
        orderText: `Smart order paused.`,
        statusText: `Your ${SmartExecutionTypeMap[order.smart_order_type]} order for symbol ${order.formatted_symbol} is paused.`
    }),
    default: (order: OrderNotificationParams) => ({
        orderText: `${order.order_mode.toUpperCase()} ${order.formatted_symbol} is ${order.order_status?.toLowerCase()}`,
        statusText: ''
    })
};